import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Swal from "sweetalert2";

import {
  Stack,
  Button,
  Container,
  Typography,
  IconButton,
  Grid,
  Input,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TextField,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { debounce } from "../../utils/debounce";
import config from "../../config";
import i18n from "../../i18n";
import { convertMessageCodeToMessage } from "../../utils/messageCodeToMessage";
import Iconify from "../../components/iconify/Iconify";
import {
  deleteTask,
  editTaskDetail,
  exportTask,
  getTasksList,
} from "../../services/task.service";
import TasksTable from "../../SharedComponent/TasksTable";
import { getAirportList } from "../../services/airport.service";
import { copyToClipBoard, getFilterKeys } from "../../utils/commonUtils";
import { getOfferList } from "../../services/offer.service";
import { getUsers } from "src/services/user.service";
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { exportFile } from "src/services/common.service";

const TABLE_HEAD = [
  {
    id: "airport",
    label: i18n.t("airport"),
    alignRight: false,
    sortable: true,
  },
  {
    id: "mandate_number",
    label: i18n.t("mandate_number"),
    alignRight: false,
    sortable: true,
  },
  { id: "assigned_to", label: i18n.t("assigned_to"), alignRight: false, sortable: true },
  { id: "resp", label: i18n.t("resp"), alignRight: false, sortable: true },
  {
    id: "information",
    label: i18n.t("information"),
    alignRight: false,
    sortable: true,
  },
  {
    id: "task_establishment_date",
    label: i18n.t("task_establishment_date"),
    alignRight: false,
    sortable: true,
  },
  {
    id: "deadline",
    label: i18n.t("deadline"),
    alignRight: false,
    sortable: true,
  },
  {
    id: "date_of_completion",
    label: i18n.t("date_of_completion"),
    alignRight: false,
    sortable: true,
  },
  {
    id: "executed",
    label: i18n.t("executed"),
    alignRight: false,
    sortable: true,
  },
  { id: "" },
];
class Tasks extends React.Component {
  constructor(props) {
    super(props);
    this.tableLimitOptions = config?.tableLimitOptions;
    this.state = {
      tasks: [],
      task: {},
      open: null,
      page: 1,
      filtersOptions: [],
      search: this.props?.search ? this.props.search : "",
      limit: this.props?.limit ? this.props.limit : 100,
      skip: 0,
      total_tasks: 0,
      expanded: false,
      tableLoader: false,
      pageLoader: false,
      filters: {},
      orderBy: { sort_key: "airport", sort_order: true },
      deleteTaskPopup: false,
      usersOptions: [],
      confirmOptions: config?.confirmOptions,
    };
    this.useStyles = {
      sticky: {
        position: "sticky",
        left: "0",
        zIndex: "5",
      },
    };
    /**when navigated from airport details taking airport id from url */
    this.airportId = window.location.href.split("=")[1];
  }

  async componentDidMount() {
    this.getAirportsForFilter();
    this.getOffersForFilter();
    this.getTasks();
    this.getUsersList();
  }

  async componentDidUpdate(prevProps, prevState) {
    if (prevProps?.search !== this.props?.search) {
      this.setState({ search: this.props?.search ? this.props.search : "" });
      this.getTasksDataOnchange();
    }
  }

  async setStateAsync(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve);
    });
  }

  async getAirportsForFilter() {
    let query = {
      minimal: true,
    };

    let response = await getAirportList(query);
    if (response.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: response?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(response?.error_code))
          : response?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      return;
    }
    let airportOptions = response?.map((airport) => {
      if (airport?._id === this.airportId) {
        this.setState({
          filters: {
            airport: { label: airport?.name, value: airport?._id },
          },
          expanded: true,
        });
      }
      return { label: airport?.name, value: airport?._id };
    });

    this.setState({
      filtersOptions: { ...this.state.filtersOptions, airportOptions },
    });
  }
  async getOffersForFilter() {
    let query = {
      minimal: true,
      unique: "mandate_number"
    };

    let response = await getOfferList(query);
    if (response.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: response?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(response?.error_code))
          : response?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      return;
    }
    let offerOptions = response?.offers?.map((offer) => {
      return { label: offer?.mandate_number, value: offer?._id };
    });

    this.setState({
      filtersOptions: { ...this.state.filtersOptions, offerOptions },
    });
  }

  async getTasks(action) {
    this.setState({
      tableLoader: true,
    });

    let query = {
      limit: this.state.limit,
      skip: this.state.skip,
      sort_key: this.state?.orderBy?.sort_key,
      sort_order: this.state?.orderBy?.sort_order ? "ASC" : "DESC",
    };

    getFilterKeys(this.state?.filters, query);

    if (this.airportId) {
      query["airport"] = this.airportId;
    }
    if (this.state.search) {
      query.search = this.state.search;
    }
    if (action === "filters") {
      query.skip = 0;
      await this.setState({
        skip: 0,
        page: 1,
      });
    }
    let tasks = await getTasksList(query);
    if (tasks.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: tasks?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(tasks?.error_code))
          : tasks?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      this.setState({
        tableLoader: false,
      });
      return;
    }

    this.setState({
      tableLoader: false,
      tasks: tasks.tasks,
      total_tasks: tasks.count,
    });
  }
  async handleDeleteTask(taskId) {
    this.setState({
      pageLoader: true,
    });
    let response = await deleteTask(taskId);

    if (response && response.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: response?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(response?.error_code))
          : response?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      return;
    }

    if (response) {
      await this.getTasks();
      this.setState({
        pageLoader: false,
      });
    }
  }

  handleExpandClick = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  handleOpenMenu = (event) => {
    this.setState({ open: event.currentTarget });
  };

  handleCloseMenu = () => {
    this.setState({ open: null });
  };

  getTasksDataOnchange = debounce(() => {
    this.getTasks("filters");
  }, config.searchDebounceDelay);

  getTasksDataOnchangeWithoutDebounce = () => {
    this.getTasks("filters");
  };

  handleTableAction = async (action, data, rowData, key, rowIndex) => {
    if (action === "pagination") {
      await this.setState({
        skip: (data - 1) * this.state.limit,
        page: data,
      });
      this.getTasks();
    } else if (action === "limit") {
      await this.setState({
        limit: data,
      });
      this.getTasks();
    } else if (action === "popOverAction") {
      this.setState({
        task: rowData,
      });
      this.handleOpenMenu(data);
    } else if (action === "sort") {
      /**These keys are inside airport key from BE,
       * the sort key for these are like : airport.[keyName]
       */
      if (config?.airportJsonKeys.includes(data.toLowerCase())) {
        await this.setState({
          orderBy: {
            sort_key:
              data === "airport"
                ? `airport`
                : `airport_${data.toLowerCase()}`,
            sort_order: !this.state.orderBy?.sort_order,
          },
        });
      } else if (data === 'task_establishment_date') {
        await this.setState({
          orderBy: {
            sort_key: 'date',
            sort_order: !this.state.orderBy?.sort_order,
          },
        });
      } else {
        await this.setState({
          orderBy: {
            sort_key: data,
            sort_order: !this.state.orderBy?.sort_order,
          },
        });
      }
      this.getTasks();
    } else if (action === "update") {
      if (rowData[key] === data) return;
      let backUpData = { rowData, rowIndex };
      let payload = { ...rowData };
      payload[key] = data;
      let tempData = this.state.tasks;
      tempData[rowIndex] = payload;
      this.setState({
        tasks: tempData,
      });
      this.updateTaskData(payload, payload?._id, backUpData);
    } else if (action === "closeMenu") {
      this.handleCloseMenu();
    } else if (action === "deleteTask") {
      this.handleCloseMenu();
      await this.setState({
        deleteTaskPopup: true,
      });
    } else if (action === "copyLink") {
      this.handleCloseMenu();
      let tempUrl = `${config.UI_BASE_URL}/tasks/${this.state.task._id}`;
      copyToClipBoard(tempUrl);
    } else if (action === "closeConfirmationPopUp") {
      this.setState({
        deleteTaskPopup: false,
      });
    } else if (action === "proceed") {
      this.setState({
        deleteTaskPopup: false,
      });
      this.handleDeleteTask(this.state.task._id);
    }
  };
  async updateTaskData(payload, taskId, backUpData) {
    let response = await editTaskDetail(taskId, payload);
    if (response && response.error) {
      let tempData = this.state.tasks;
      tempData[backUpData?.rowIndex] = backUpData?.rowData;
      this.setState({
        tasks: tempData,
      });
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: response?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(response?.error_code))
          : response?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      return;
    }

    if (response && response.task) {
      // Swal toast
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: i18n.t("success"),
        text: i18n.t("task_updated_successfully"),
        showConfirmButton: false,
        timer: config.toastDelay,
        timerProgressBar: true,
        customClass: {
          container: "my-swal",
        },
      });
    }
  }

  async getUsersList() {
    let query = {
      internal: true,
      minimal: true
    };

    let users = await getUsers(query);
    if (users.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: users?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(users?.error_code))
          : users?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      return;
    }
    let tempOptions = users.users?.map((option) => {
      return { label: option?.name, value: option?._id };
    });
    await this.setState({
      usersOptions: tempOptions,
    });
  }

  async exportTaskData() {
    exportFile({
      filters: this.state.filters,
      search: this.state.search,
      sort_key: this.state?.orderBy?.sort_key,
      sort_order: this.state?.orderBy?.sort_order ? "ASC" : "DESC",
      apiFunction: exportTask,
      fileName: "tasks",
      onStart: () => this.setState({ pageLoader: true }),
      onComplete: () => this.setState({ pageLoader: false }),
    });
  }


  render() {

    if (this.props?.hideIfNoResults && this.state.tasks?.length === 0 && !this.state.tableLoader) {
      return null;
    }

    return (
      <Container maxWidth="xxl">
        <Helmet>
          <title>
            {" "}
            {i18n.t("tasks")} | {config.APPLICATION_NAME}{" "}
          </title>
        </Helmet>

        {(this.props?.showTitle) ? (
          <Typography variant="h4" color={'black'}>
            {i18n.t("tasks")}
          </Typography>
        ) : null}

        {(this.props?.hideActionsBar) ? null : (
          <Grid fullWidth>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <div style={{ width: "100%", marginRight: "10px" }}>
                <Accordion
                  TransitionProps={{ unmountOnExit: true }}
                  expanded={this.state.expanded}
                  sx={{
                    background: "white",
                  }}
                >
                  <AccordionSummary
                    style={{ background: "white" }}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                    sx={{ margin: 0 }}
                  >
                    <Input
                      autoFocus
                      fullWidth
                      disableUnderline
                      placeholder="Search…"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      startAdornment={
                        <InputAdornment position="start">
                          <Iconify
                            icon="eva:search-fill"
                            sx={{ color: "text.disabled", width: 20, height: 20 }}
                          />
                        </InputAdornment>
                      }
                      onChange={(e) => {
                        this.setState({
                          search: e?.target?.value,
                        });
                        this.getTasksDataOnchange();
                      }}
                      sx={{ mr: 1, fontWeight: "fontWeightBold" }}
                    />
                    <IconButton
                      onClick={() => {
                        this.handleExpandClick();
                      }}
                    >
                      <FilterAltIcon />
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      <Grid container spacing={3} mb={2}>
                        <Grid item xs={4}>
                          <Stack spacing={1}>
                            <Autocomplete
                              disablePortal
                              noOptionsText={i18n.t('no_options')}
                              options={
                                !this.state?.filtersOptions?.airportOptions
                                  ? [{ label: "Loading...", id: 0 }]
                                  : this.state?.filtersOptions?.airportOptions
                              }
                              onChange={async (option, value) => {
                                await this.setStateAsync({
                                  filters: {
                                    ...this.state?.filters,
                                    airport: {
                                      label: value?.label,
                                      value: value?.value,
                                    },
                                  },
                                });
                                this.getTasksDataOnchangeWithoutDebounce();
                              }}
                              value={this.state?.filters?.airport?.label || ""}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={i18n.t("airport")}
                                />
                              )}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={4}>
                          <Stack spacing={1}>
                            <Autocomplete
                              required
                              disablePortal
                              options={
                                !this.state?.filtersOptions?.offerOptions
                                  ? [{ label: "Loading...", id: 0 }]
                                  : this.state?.filtersOptions?.offerOptions
                              }
                              onChange={async (option, value) => {
                                await this.setStateAsync({
                                  filters: {
                                    ...this.state?.filters,
                                    mandate_number: {
                                      label: value?.label,
                                      value: value ? value?.value : "",
                                    },
                                  },
                                });
                                this.getTasksDataOnchangeWithoutDebounce();
                              }}
                              value={
                                this.state?.filters?.mandate_number?.label || ""
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={i18n.t("mandate_number")}
                                />
                              )}
                            />
                          </Stack>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Stack spacing={1}>
                            <Autocomplete
                              disablePortal
                              noOptionsText={i18n.t('no_options')}
                              opOpen={() => {
                                this.getUsersList();
                              }}
                              options={
                                !this?.state.usersOptions
                                  ? [{ label: "Loading...", id: 0 }]
                                  : this?.state.usersOptions
                              }
                              onChange={async (option, value) => {
                                let filtersData = JSON.parse(JSON.stringify(this.state?.filters));
                                filtersData['resp'] = {
                                  label: value?.label,
                                  value: value ? value?.value : "",
                                }
                                await this.setStateAsync({
                                  filters: filtersData
                                });
                                this.getTasksDataOnchangeWithoutDebounce();
                              }}
                              value={this.state.filters?.resp?.label || ""}
                              renderInput={(params) => (
                                <TextField
                                  required
                                  {...params}
                                  label={i18n.t("resp")}
                                />
                              )}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            disablePortal
                            noOptionsText={i18n.t('no_options')}
                            onOpen={async () => {
                              await this.getUsersList();
                            }}
                            options={
                              !this.state.usersOptions
                                ? [{ label: "Loading...", id: 0 }]
                                : this.state.usersOptions
                            }
                            onChange={async (option, value) => {
                              let filtersData = JSON.parse(JSON.stringify(this.state?.filters));
                              filtersData['assigned_to'] = {
                                label: value?.label,
                                value: value ? value?.value : "",
                              }
                              await this.setStateAsync({
                                filters: filtersData
                              });
                              this.getTasksDataOnchangeWithoutDebounce();
                            }}
                            value={i18n.t(this.state.filters?.assigned_to?.label) || ""}
                            renderInput={(params) => (
                              <TextField
                                required
                                {...params}
                                label={i18n.t("assigned_to")}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            disablePortal
                            noOptionsText={i18n.t('no_options')}
                            options={
                              !this.state.confirmOptions
                                ? [{ label: "Loading...", id: 0 }]
                                : this.state.confirmOptions
                            }
                            onChange={async (option, value) => {
                              let filtersData = JSON.parse(JSON.stringify(this.state?.filters));
                              filtersData['executed'] = {
                                label: value?.label,
                                value: value ? value?.value : "",
                              }
                              await this.setStateAsync({
                                filters: filtersData
                              });
                              this.getTasksDataOnchangeWithoutDebounce();
                            }}
                            value={i18n.t(this.state.filters?.executed?.label) || ""}
                            renderInput={(params) => (
                              <TextField
                                required
                                {...params}
                                label={i18n.t("executed")}
                              />
                            )}
                          />
                        </Grid>

                      </Grid>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </div>
              <Button
                variant="outlined"
                startIcon={<SystemUpdateAltIcon />}
                style={{ marginRight: '10px' }}
                className="BtnWidth"
                onClick={() => {
                  this.exportTaskData();
                }}
              >
                {i18n.t("export_tasks")}
              </Button>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                className="BtnWidth"
                onClick={() => {
                  this.props.navigate("/tasks/add");
                }}
              >
                {i18n.t("task")}
              </Button>
            </Stack>
          </Grid>
        )}

        <TasksTable
          data={this.state}
          onAction={this.handleTableAction}
          tableHeaders={TABLE_HEAD}
          hidePagination={this.props?.hidePagination}
        />

        <Backdrop
          sx={{
            color: "#e5e5e5",
            zIndex: (theme) => theme.zIndex.drawer + 1000,
          }}
          open={this.state?.pageLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    );
  }
}

// Wrap and export
export default function (props) {
  const navigate = useNavigate();
  const params = useParams();

  return <Tasks {...props} navigate={navigate} params={params} />;
}
